/*
  MyDecompte.jsx
  ----------------
  Interface d'affichage des décomptes.
*/
import { useEffect, useState } from "react";
import {
  Card,
  Text,
  Stack,
  Center,
  Loader,
  TextInput,
  Group,
  Badge,
  Divider,
  Modal,
  Button,
  Pagination,
} from "@mantine/core";
import { get } from "../tools/Request";
import { useUser } from "../tools/Context";
import { IconBook, IconEye } from "@tabler/icons-react";
import DisplayDecompteWorkflow from "../tools/DisplayDecompteWorkflow";
import { useNavigate, useSearchParams } from "react-router-dom";
import DisplayDecompteProof from "../tools/DisplayDecompteProof";
import downloadFromBackend from "../tools/DownloadFromBackend";

const ITEMS_PER_PAGE = 5;

export default function MyDecompte() {
  const [decomptes, setDecomptes] = useState([]);
  const [filteredDecomptes, setFilteredDecomptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [marketSearch, setMarketSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const user = useUser();
  const { token, isratp } = user;
  const [modalProof, setModalProofOpen] = useState(false);
  const [selectedDecompteId, setSelectedDecompteId] = useState(null);
  const [modalDocuments, setModalDocumentsOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const navigate = useNavigate();

  const primaryColor = isratp ? "#0a0082" : "#ff7e2e";

  useEffect(() => {
    const fetchDecomptes = async () => {
      try {
        const result = await get("decompte/my-decomptes", null, token);
        setDecomptes(result);
        setFilteredDecomptes(result);
      } catch (err) {
        console.error("Error fetching decomptes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDecomptes();
  }, [token]);

  // Filter logic
  useEffect(() => {
    const filtered = decomptes.filter(
      (d) =>
        (d.name || "").toLowerCase().includes(search.toLowerCase()) &&
        (!marketSearch || (d.market_number || "").toLowerCase().includes(marketSearch.toLowerCase()))
    );
    setFilteredDecomptes(filtered);
    setCurrentPage(1);
  }, [search, marketSearch, decomptes]);

  const openProofModal = (id) => {
    setSelectedDecompteId(id);
    setModalProofOpen(true);
  };

  const openDocumentsModal = async (decompteId) => {
    try {
      const docs = await get(`decompte/file/decompte-documents/${decompteId}`, null, token);
      setDocuments(docs);
      setModalDocumentsOpen(true);
    } catch (err) {
      console.error("Erreur lors du chargement des documents :", err);
    }
  };

  const totalPages = Math.ceil(filteredDecomptes.length / ITEMS_PER_PAGE);
  const paginatedDecomptes = filteredDecomptes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Center mt={60}>
        <Loader />
      </Center>
    );
  }

  return (
    <Center>
      <Stack w="100%" mt={40}>
        <Group grow mb="md">
          <TextInput
            placeholder="Filtrer par nom de décompte"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <TextInput
            placeholder="Filtrer par numéro de marché"
            value={marketSearch}
            onChange={(e) => setMarketSearch(e.currentTarget.value)}
          />
        </Group>

        {filteredDecomptes.length === 0 ? (
          <Text>Aucun décompte trouvé.</Text>
        ) : (
          <>
            {paginatedDecomptes.map((decompte) => (
              <Card key={decompte.id} shadow="sm" padding="md" radius="md" withBorder>
                <Group position="apart" align="center">
                  <Stack gap={2}>
                    <Text fw={600}>{decompte.name}</Text>
                    <Text size="sm" c="dimmed">
                      Dernière modification :{" "}
                      <strong>
                        {decompte.last_edit_timestamp
                          ? new Date(decompte.last_edit_timestamp).toLocaleString("fr-FR")
                          : "—"}
                      </strong>
                    </Text>
                  </Stack>

                  <Group>
                    <Badge
                      color={primaryColor}
                      style={{ cursor: "pointer" }}
                      variant="light"
                      leftSection={<IconEye size={18} />}
                      onClick={() => openDocumentsModal(decompte.id)}
                    >
                      Voir les documents
                    </Badge>
                    {user.role !== "titulaire de marché" && (
                      <Badge
                        color={primaryColor}
                        style={{ cursor: "pointer" }}
                        variant="light"
                        leftSection={<IconBook size={18} />}
                        onClick={() => openProofModal(decompte.id)}
                      >
                        Historique
                      </Badge>
                    )}
                  </Group>
                </Group>

                <Text
                  size="sm"
                  c="dimmed"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => navigate(`/MarketDisplay?market_number=${decompte.market_number}`)}
                >
                  Marché : <strong>{decompte.market_number || "—"}</strong>
                </Text>

                {decompte.linked_decompte_name && (
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setSearch(decompte.linked_decompte_name)}
                  >
                    Décompte lié : <strong>{decompte.linked_decompte_name}</strong>
                  </Text>
                )}

                <Divider mt={15} />
                <DisplayDecompteWorkflow decompteId={decompte.id} decompteName={decompte.name} />
              </Card>
            ))}

            <Center>
              {totalPages > 1 && (
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                  mt="lg"
                  color={primaryColor}
                />
              )}
            </Center>
          </>
        )}
      </Stack>

      <Modal size={1000} opened={modalProof} onClose={() => setModalProofOpen(false)} title="Historique du décompte">
        {selectedDecompteId && <DisplayDecompteProof decompteId={selectedDecompteId} />}
      </Modal>

      <Modal
        size="lg"
        opened={modalDocuments}
        onClose={() => setModalDocumentsOpen(false)}
        title="Documents du décompte"
      >
        <Stack>
          {documents.length === 0 ? (
            <Text>Aucun document trouvé.</Text>
          ) : (
            documents.map((doc) => (
              <Group key={doc.id} position="apart">
                <Text>{doc.name}</Text>
                <Button size="xs" variant="light" onClick={() => downloadFromBackend(doc.id)}>
                  Télécharger
                </Button>
              </Group>
            ))
          )}
        </Stack>
      </Modal>
    </Center>
  );
}
